import { useEffect, useRef } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface IdleTimeoutOverlayProps {
  /** Seconds remaining until timeout */
  remainingSeconds: number;
  /** Called when user clicks "Stay logged in" */
  onStayLoggedIn: () => void;
}

/** Full-screen overlay with a countdown warning before auto-logout */
export function IdleTimeoutOverlay({ remainingSeconds, onStayLoggedIn }: IdleTimeoutOverlayProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the "Stay logged in" button so Enter key works immediately
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const timeDisplay = minutes > 0
    ? `${minutes}m ${seconds.toString().padStart(2, '0')}s`
    : `${seconds}s`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div
        className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in zoom-in-95 duration-300"
        role="alertdialog"
        aria-label="Session time out warning"
      >
        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-5">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>

          <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
            Are you still there?
          </h2>

          <p className="text-sm text-[var(--text-secondary)] mb-6 max-w-xs">
            You've been inactive for a while. For security, you'll be
            logged out automatically.
          </p>

          {/* Countdown */}
          <div className="flex items-center gap-2 mb-6 px-5 py-3 bg-red-500/5 border border-red-500/20 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span className="text-sm font-mono font-semibold text-red-400 tabular-nums">
              Logging out in {timeDisplay}
            </span>
          </div>

          <Button
            ref={buttonRef}
            onClick={onStayLoggedIn}
            size="lg"
            className="w-full text-base font-semibold"
          >
            I'm Still Here
          </Button>

          <p className="text-[11px] text-[var(--text-tertiary)] mt-3">
            Press Enter or click the button to stay logged in
          </p>
        </div>
      </div>
    </div>
  );
}
